import React, { useEffect, useState } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
  Divider,
  Switch,
  Tag,
  Alert,
  Skeleton,
  InputNumber,
  App,
} from 'antd';
import {
  SaveOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import {
  useGetEmailConfigQuery,
  useCreateEmailConfigMutation,
  useUpdateEmailConfigMutation,
  useTestEmailConfigMutation,
} from '../../services/facturacionApi';

const { Title, Text } = Typography;

const EmailConfigTab: React.FC = () => {
  const { message: messageApi } = App.useApp();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [emailForm] = Form.useForm();
  const [useCustomSmtp, setUseCustomSmtp] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');

  // Email Config API hooks
  const { data: emailConfig, isLoading: loadingEmailConfig } = useGetEmailConfigQuery();
  const [createEmailConfig, { isLoading: creatingEmailConfig }] = useCreateEmailConfigMutation();
  const [updateEmailConfig, { isLoading: updatingEmailConfig }] = useUpdateEmailConfigMutation();
  const [testEmailConfig, { isLoading: testingEmailConfig }] = useTestEmailConfigMutation();

  // Load email config into form
  useEffect(() => {
    if (emailConfig) {
      setUseCustomSmtp(emailConfig.useCustomSmtp);
      emailForm.setFieldsValue({
        useCustomSmtp: emailConfig.useCustomSmtp,
        smtpHost: emailConfig.smtpHost,
        smtpPort: emailConfig.smtpPort,
        smtpUsername: emailConfig.smtpUsername,
        smtpUseSsl: emailConfig.smtpUseSsl,
        senderEmail: emailConfig.senderEmail,
        senderName: emailConfig.senderName,
        replyToEmail: emailConfig.replyToEmail,
        facturaEmailSubject: emailConfig.facturaEmailSubject,
        facturaEmailTemplate: emailConfig.facturaEmailTemplate,
      });
    }
  }, [emailConfig, emailForm]);

  const handleSaveEmailConfig = async () => {
    try {
      const values = await emailForm.validateFields();

      const configData = {
        useCustomSmtp: values.useCustomSmtp || false,
        smtpHost: values.smtpHost,
        smtpPort: values.smtpPort,
        smtpUsername: values.smtpUsername,
        smtpPassword: values.smtpPassword,
        smtpUseSsl: values.smtpUseSsl ?? true,
        senderEmail: values.senderEmail,
        senderName: values.senderName,
        replyToEmail: values.replyToEmail,
        facturaEmailSubject: values.facturaEmailSubject,
        facturaEmailTemplate: values.facturaEmailTemplate,
      };

      if (emailConfig?.id) {
        await updateEmailConfig(configData).unwrap();
        messageApi.success('Configuración de email actualizada correctamente');
      } else {
        await createEmailConfig(configData).unwrap();
        messageApi.success('Configuración de email creada correctamente');
      }
    } catch (error: any) {
      console.error('Error saving email config:', error);
      messageApi.error(error?.data?.message || 'Error al guardar configuración de email');
    }
  };

  const handleTestEmail = async () => {
    if (!testEmailAddress) {
      messageApi.warning('Ingrese un correo de prueba');
      return;
    }

    try {
      const result = await testEmailConfig({ testEmail: testEmailAddress }).unwrap();
      if (result.success) {
        messageApi.success(result.message || 'Correo de prueba enviado correctamente');
      } else {
        messageApi.error(result.error || 'Error al enviar correo de prueba');
      }
    } catch (error: any) {
      console.error('Error testing email:', error);
      messageApi.error(error?.data?.message || 'Error al probar configuración de email');
    }
  };

  if (loadingEmailConfig) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  return (
    <div>
      <Title level={4}>Configuración de Correo Electrónico</Title>
      <Text type="secondary">
        Configure cómo se enviarán los correos electrónicos de facturas desde su restaurante.
      </Text>
      <Divider />

      <Form form={emailForm} layout="vertical">
        {/* Current Status */}
        <div style={{ marginBottom: 24 }}>
          {!useCustomSmtp ? (
            <Tag icon={<InfoCircleOutlined />} color="default" style={{ fontSize: 14, padding: '4px 12px' }}>
              Usando Configuración por Defecto
            </Tag>
          ) : emailConfig?.isVerified ? (
            <>
              <Tag icon={<CheckCircleOutlined />} color="success" style={{ fontSize: 14, padding: '4px 12px' }}>
                Configuración Verificada
              </Tag>
              {emailConfig?.verifiedAt && (
                <Text type="secondary" style={{ marginLeft: 12 }}>
                  Verificada el {formatInTimeZone(emailConfig.verifiedAt, 'DD/MM/YYYY')}
                </Text>
              )}
            </>
          ) : (
            <Tag icon={<ExclamationCircleOutlined />} color="warning" style={{ fontSize: 14, padding: '4px 12px' }}>
              Pendiente de Verificación
            </Tag>
          )}
        </div>

        {/* Use Custom SMTP Switch */}
        <Form.Item
          name="useCustomSmtp"
          label="Usar Servidor SMTP Personalizado"
          valuePropName="checked"
        >
          <Switch
            onChange={(checked) => setUseCustomSmtp(checked)}
            checkedChildren="Personalizado"
            unCheckedChildren="Por Defecto"
          />
        </Form.Item>

        {!useCustomSmtp && (
          <Alert
            message="Configuración por Defecto"
            description="Los correos se enviarán utilizando el servidor SMTP del sistema. Active la opción anterior para configurar su propio servidor SMTP."
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        {useCustomSmtp && (
          <>
            <Title level={5}>Servidor SMTP</Title>

            <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
              <Form.Item
                name="smtpHost"
                label="Host SMTP"
                rules={[{ required: useCustomSmtp, message: 'Ingrese el host SMTP' }]}
                style={{ flex: 2, minWidth: isMobile ? '100%' : 250 }}
              >
                <Input placeholder="smtp.gmail.com, smtp.office365.com..." />
              </Form.Item>

              <Form.Item
                name="smtpPort"
                label="Puerto"
                rules={[{ required: useCustomSmtp, message: 'Ingrese el puerto' }]}
                style={{ flex: 1, minWidth: isMobile ? '100%' : 100 }}
              >
                <InputNumber placeholder="587" min={1} max={65535} style={{ width: '100%' }} />
              </Form.Item>
            </Space>

            <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
              <Form.Item
                name="smtpUsername"
                label="Usuario SMTP"
                rules={[{ required: useCustomSmtp, message: 'Ingrese el usuario' }]}
                style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
              >
                <Input placeholder="usuario@ejemplo.com" />
              </Form.Item>

              <Form.Item
                name="smtpPassword"
                label="Contraseña SMTP"
                style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
                extra={emailConfig?.hasSmtpPassword ? 'Contraseña ya configurada. Deje vacío para mantenerla.' : undefined}
              >
                <Input.Password placeholder={emailConfig?.hasSmtpPassword ? '••••••••' : 'Contraseña'} />
              </Form.Item>
            </Space>

            <Form.Item
              name="smtpUseSsl"
              label="Usar SSL/TLS"
              valuePropName="checked"
              initialValue={true}
            >
              <Switch checkedChildren="SSL" unCheckedChildren="Sin SSL" defaultChecked />
            </Form.Item>

            <Divider />
          </>
        )}

        {useCustomSmtp && (
          <>
            <Title level={5}>Información del Remitente</Title>

            <Space size="large" style={{ width: '100%' }} direction={isMobile ? 'vertical' : 'horizontal'}>
              <Form.Item
                name="senderEmail"
                label="Email del Remitente"
                rules={[
                  { type: 'email', message: 'Email inválido' },
                ]}
                style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
              >
                <Input placeholder="facturas@mirestaurante.com" />
              </Form.Item>

              <Form.Item
                name="senderName"
                label="Nombre del Remitente"
                style={{ flex: 1, minWidth: isMobile ? '100%' : 250 }}
                extra="Este nombre aparecerá como remitente en los correos de facturas"
              >
                <Input placeholder="Mi Restaurante - Facturación" />
              </Form.Item>
            </Space>

            <Form.Item
              name="replyToEmail"
              label="Email de Respuesta (Reply-To)"
              rules={[
                { type: 'email', message: 'Email inválido' },
              ]}
            >
              <Input placeholder="contacto@mirestaurante.com" />
            </Form.Item>

            <Divider />
          </>
        )}

        {/* Test Email Section */}
        <Title level={5}>Probar Configuración</Title>
        <Text type="secondary">
          Envíe un correo de prueba para verificar que la configuración es correcta.
        </Text>

        <div style={{ marginTop: 16, marginBottom: 24 }}>
          <Space size="middle" direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: '100%' }}>
            <Input
              placeholder="correo@prueba.com"
              value={testEmailAddress}
              onChange={(e) => setTestEmailAddress(e.target.value)}
              style={{ width: isMobile ? '100%' : 300 }}
            />
            <Button
              icon={<SendOutlined />}
              onClick={handleTestEmail}
              loading={testingEmailConfig}
            >
              Enviar Prueba
            </Button>
          </Space>
        </div>

        {emailConfig?.verificationError && (
          <Alert
            message="Error en Última Verificación"
            description={emailConfig.verificationError}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        <Divider />

        {/* Save Button */}
        <Form.Item>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveEmailConfig}
            loading={creatingEmailConfig || updatingEmailConfig}
            size="large"
            block={isMobile}
          >
            Guardar Configuración de Email
          </Button>
        </Form.Item>
      </Form>

    </div>
  );
};

export default EmailConfigTab;
